import {Loader, Modal} from '@mantine/core';
import {Notifications} from '@mantine/notifications';
import {WhiteBgButtonThin} from '@packages/ui';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {
  DefaultExperience,
  DevelopmentExperience,
  type DevelopmentExperienceType,
} from '../../../../../shared/components/DevelopmentExperiences';
import {useHandleDevelopmentExperiences} from '../../../usecase/useHandleDevelopmentExperiences';

type Props = {
  opened: boolean;
  close: () => void;
  devExperienceBody: string;
  setDevExperienceBody: (text: string) => void;
  devExperienceHashtagIds: number[];
  setDevExperienceHashtagIds: (hashtags: number[]) => void;
};

export const AddDevelopExperienceModal: React.FC<Props> = ({
  opened,
  close,
  devExperienceBody,
  setDevExperienceBody,
  devExperienceHashtagIds,
  setDevExperienceHashtagIds,
}) => {
  const [isLoading, setIsLoading] = useState(true);

  const onClose = () => {
    close();
    setDevExperienceBody('');
    setDevExperienceHashtagIds([]);
    setIsLoading(true);
  };

  const {
    form,
    submitPut,
    transformValues,
    categories,
    languages,
    frameworks,
    others,
  } = useHandleDevelopmentExperiences({
    onPutSuccessFunc() {
      onClose();
    },
  });

  const experiences = useMemo(() => {
    return form.values.experiences.map(
      (experience): DevelopmentExperienceType => {
        return {
          id: experience.id,
          categoryID: experience.categoryTag?.hashtagID,
          body: experience.body,
          startDate: experience.startDate,
          endDate: experience.endDate,
          programmingLanguageTags: experience.programmingLanguageTags,
          libraryFrameworkTags: experience.libraryFrameworkTags,
          otherTags: experience.otherTags,
        };
      },
    );
  }, [form.values]);

  const onExperiencesChange = (experiences: DevelopmentExperienceType[]) => {
    const newExperiences = experiences.map((experience) => {
      return {
        id: experience.id,
        categoryTag: {
          hashtagID: experience.categoryID ?? 0,
          hashtagName: '',
        },
        body: experience.body,
        startDate: experience.startDate ?? '',
        endDate: experience.endDate ?? '',
        programmingLanguageTags: experience.programmingLanguageTags ?? [],
        libraryFrameworkTags: experience.libraryFrameworkTags ?? [],
        otherTags: experience.otherTags ?? [],
      };
    });

    form.setFieldValue('experiences', newExperiences);
  };

  const onUpdate = useCallback(
    (index: number, experience: DevelopmentExperienceType) => {
      const newExperiences = [...experiences];
      newExperiences[index] = {
        ...experience,
      };
      onExperiencesChange(newExperiences);
    },
    [experiences, onExperiencesChange],
  );

  // Initial set form value
  useEffect(() => {
    if (!isLoading || devExperienceBody.length === 0) return;

    const newItem = {...DefaultExperience, body: devExperienceBody};
    if (languages.length > 0) {
      newItem.programmingLanguageTags = languages
        .map((language) => {
          if (devExperienceHashtagIds.includes(Number(language.value))) {
            return {
              hashtagID: Number(language.value),
              hashtagName: '',
            };
          }

          return null;
        })
        .filter((item) => item !== null);
    }

    if (frameworks) {
      newItem.libraryFrameworkTags = frameworks
        .map((framework) => {
          if (devExperienceHashtagIds.includes(Number(framework.value))) {
            return {
              hashtagID: Number(framework.value),
              hashtagName: '',
            };
          }

          return null;
        })
        .filter((item) => item !== null);
    }

    if (others) {
      newItem.otherTags = others
        .map((other) => {
          if (devExperienceHashtagIds.includes(Number(other.value))) {
            return {
              hashtagID: Number(other.value),
              hashtagName: '',
            };
          }

          return null;
        })
        .filter((item) => item !== null);
    }

    const newExperiences = [newItem, ...experiences];
    onExperiencesChange(newExperiences);

    setIsLoading(false);
  }, [devExperienceBody, devExperienceHashtagIds]);

  return (
    <>
      <Modal
        size="lg"
        title="開発経験の具体事例"
        opened={opened}
        onClose={onClose}
      >
        {isLoading ? (
          <Loader color="gray" className="m-auto m-y-50" />
        ) : (
          <form>
            <DevelopmentExperience
              index={0}
              experience={experiences[0] ?? DefaultExperience}
              categories={categories}
              languages={languages}
              frameworks={frameworks}
              others={others}
              onUpdate={onUpdate}
              errors={form.errors}
              errorsKey="experiences"
              showAlertMessage={false}
              canDelete={false}
            />
            <WhiteBgButtonThin
              type="submit"
              className="w-full mt-32 mx-auto"
              label="保存する"
              isPrimary={false}
              as="button"
              onClick={form.onSubmit((values) => {
                const newValues = transformValues(values);
                submitPut(newValues);
              })}
            />
          </form>
        )}
      </Modal>
      <Notifications position={'top-right'} />
    </>
  );
};
